"use client";

import { useLanguage } from "@/i18n/LanguageContext";

interface AboutServiceSectionProps {
  badge: string;
  imageSrc: string;
  imageAlt: string;
  heading: string;
  lead: string;
  bullets: string[];
  closing: string;
  ctaAnchor?: string;
}

export default function AboutServiceSection({
  badge,
  imageSrc,
  imageAlt,
  heading,
  lead,
  bullets,
  closing,
  ctaAnchor = "#faq",
}: AboutServiceSectionProps) {
  const { t } = useLanguage();

  return (
    <section className="bg-white py-20 md:py-[80px]">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-[60px] items-center">
          {/* LEFT: Image */}
          <div className="order-1 md:order-1">
            <img
              src={imageSrc}
              alt={imageAlt}
              width={550}
              height={420}
              loading="lazy"
              className="w-full max-w-[550px] rounded-xl shadow-md"
            />
          </div>

          {/* RIGHT: Text */}
          <div className="order-2 md:order-2">
            <span className="inline-block bg-[#ea580c] text-white uppercase text-xs tracking-[1px] font-semibold px-3.5 py-1.5 rounded-2xl">
              {badge}
            </span>

            <h2 className="text-[#064e3b] text-[32px] leading-tight font-bold mt-4">
              {heading}
            </h2>

            <p className="text-[#374151] text-base leading-[1.7] mt-4">
              {lead}
            </p>

            <ul className="mt-5 space-y-3">
              {bullets.map((item) => (
                <li key={item} className="flex items-start gap-3 text-[#374151] text-base leading-[1.6]">
                  <i className="ri-check-line text-[#047857] text-[15px] mt-1 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <p className="text-gray-600 text-sm italic mt-4">
              {closing}
            </p>

            <a
              href={ctaAnchor}
              className="inline-block text-[#047857] text-sm font-semibold mt-4 hover:underline"
            >
              {t("common.readMore")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}